import { useRouter } from "next/router";

import { DotsVerticalIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import { linkify } from "@logics/utils/urlHandler";
import Avatar from "@domains/common/views/components/Avatar/Avatar";
import { useAttachLinkClickHandler } from "@logics/hooks/useAttachLinkClickHandler";
import { useReplyMenu } from "@domains/project/hooks/useReplyMenu";
import WebViewBridge from "@logics/utils/webViewBridge";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";

import * as S from "./ReplyCard.style";

interface ReplyCardProps {
  isMySelf: boolean;
  projectId?: string;
  commentId?: number;
  replyId?: number;
  authorName: string;
  authorId: number;
  authorProfile?: string;
  commenterName: string;
  repliedPayload: string;
  repliedAt: string;
}

const ReplyCard = ({
  isMySelf,
  projectId,
  commentId,
  replyId,
  authorId,
  authorName = "the commenter",
  authorProfile,
  commenterName,
  repliedPayload,
  repliedAt,
}: ReplyCardProps) => {
  const { push } = useRouter();

  const { handleClickMenu } = useReplyMenu({
    isMySelf,
    projectId,
    commentId,
    replyId,
    commenterName,
    payload: repliedPayload,
  });

  useAttachLinkClickHandler({ id: replyId || "" });

  const moveToUserProfilePage = (userId: number) => {
    if (!userId) return;

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToUserProfile(userId);
    } else {
      push({
        pathname: `${NAVIGATION_ROUTE.PROFILE.HREF}/${userId}`,
        query: NAVIGATION_ROUTE.PROFILE.QUERY,
      });
    }
  };

  return (
    <S.Container>
      <Avatar
        profile={{ url: authorProfile || "", id: authorId }}
        size="m"
        style={{ cursor: "pointer" }}
        onClick={() => moveToUserProfilePage(authorId)}
      />
      <S.ContentWrapper>
        <S.MenuIconWrapper
          $shouldShow
          $size={16}
          role="button"
          onClick={handleClickMenu}
        >
          <DotsVerticalIcon size={16} strokeColor={grayScale.white} />
        </S.MenuIconWrapper>

        <S.WriterAndTime>
          <S.Writer
            name="s"
            as="button"
            onClick={() => moveToUserProfilePage(authorId)}
          >
            {authorName}
          </S.Writer>
          <S.Time name="s">{repliedAt}</S.Time>
        </S.WriterAndTime>

        <S.CommentContent
          name="m"
          dangerouslySetInnerHTML={{
            __html: linkify(repliedPayload, String(replyId)),
          }}
        />
      </S.ContentWrapper>
    </S.Container>
  );
};

export default ReplyCard;
